/** Per-field diff outcome comparing a pre-enrichment ("before") value to the enriched ("after") value. */
export type DiffStatus = 'enriched' | 'modified' | 'removed' | 'unchanged';

/** Background color per diff status — shared by `DiffCell` and any other cell-level renderer (e.g. `HighlightCardList`) that needs the same accent. */
export const DIFF_STATUS_BACKGROUND_CLASS: Record<DiffStatus, string> = {
  enriched: 'bg-success-50',
  modified: 'bg-warning-50',
  removed: 'bg-error-50',
  unchanged: '',
};

/** True when a value should be treated as "no value" for diffing (null/undefined/empty string/empty array). */
function isEmptyValue(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/** Normalizes a value to a comparable string — trims whitespace, sorts array contents so ordering doesn't cause a false "modified". */
function normalize(value: unknown): string {
  if (Array.isArray(value)) {
    return (value as unknown[])
      .map((v) => normalize(v))
      .sort()
      .join(' ');
  }
  if (typeof value === 'string') return value.trim();
  return JSON.stringify(value);
}

/**
 * Compares a before/after pair for one field and classifies the change:
 * no before + has after → enriched, has before + different after → modified,
 * has before + empty after → removed, otherwise unchanged.
 */
export function computeDiffStatus(beforeValue: unknown, afterValue: unknown): DiffStatus {
  const hadBefore = !isEmptyValue(beforeValue);
  const hasAfter = !isEmptyValue(afterValue);

  if (!hadBefore && hasAfter) return 'enriched';
  if (hadBefore && !hasAfter) return 'removed';
  if (hadBefore && hasAfter && normalize(beforeValue) !== normalize(afterValue)) return 'modified';
  return 'unchanged';
}

/** Builds an id → item lookup map for joining a "before" list against an "after" list locally. */
export function mapProductsById<T>(items: T[], getId: (item: T) => string): Map<string, T> {
  const map = new Map<string, T>();
  for (const item of items) {
    map.set(getId(item), item);
  }
  return map;
}
